import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  ChangePasswordPayload,
  HookParams,
  LoginHistoryEntry,
  UserProfile,
} from '@/libs/interfaces'
import { accountApi } from '@/services/api'

export interface AccountParams {
  orgID: string
  userID: string
}

export function useAccountProfile({ params, enabled }: HookParams<AccountParams>) {
  return useQuery<UserProfile>({
    queryKey: QUERY_KEYS.account.profile(params.userID),
    queryFn: () => accountApi.getProfile(params.orgID, params.userID),
    enabled: (enabled ?? true) && !!params.orgID && !!params.userID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export interface UpdateAccountProfilePayload {
  userID: string
  firstName: string
  lastName: string
  email: string
}

export function useAccountProfileUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ userID, ...data }: UpdateAccountProfilePayload) =>
      accountApi.updateProfile(userID, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.account.profile(variables.userID),
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export interface AccountChangePasswordPayload extends ChangePasswordPayload {
  userID: string
}

export function useAccountChangePassword() {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ userID, ...payload }: AccountChangePasswordPayload) =>
      accountApi.changePassword(userID, payload),
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('changePassword.updateFailedToast')))
    },
  })
}

export function useAccountLoginHistory({ params, enabled }: HookParams<AccountParams>) {
  return useQuery<LoginHistoryEntry[]>({
    queryKey: QUERY_KEYS.account.loginHistory(params.userID),
    queryFn: () => accountApi.getLoginHistory(params.orgID, params.userID),
    enabled: (enabled ?? true) && !!params.orgID && !!params.userID,
    staleTime: STALE_TIME['1Hour'],
  })
}
